import { useOutletContext } from 'react-router-dom';
import { useVertical } from '@/shared/hooks/useVertical';
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  User,
  MapPin,
  DollarSign,
  Stethoscope,
  Rocket,
  TrendingUp,
  BarChart3,
  Link2,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { Card, Badge, EmptyState, SectionLabel, ProgressBar } from '@/shared/ui';
import { uploadApi, type UploadType } from '../api/uploadApi';
import { UploadCard } from '../components/UploadCard';
import { invalidateUploadDependents } from '../utils/invalidateUploadDependents';
import { strategyKeys } from '../queryKeys';
import styles from './PracticeDataStep.module.css';

const UPLOAD_TYPES: Array<{
  id: UploadType;
  label: string;
  desc: string;
  impact: string;
  icon: LucideIcon;
  example: (practiceSoftwareAbbreviation: string) => string;
}> = [
  {
    id: 'demographics',
    label: 'Patient Demographics',
    desc: 'Age, gender distribution',
    impact: 'Unlocks Practice vs. Market comparison',
    icon: User,
    example: (sw: string) => `Export from ${sw} → Reports → Patient Demographics`,
  },
  {
    id: 'geographic',
    label: 'Geographic Distribution',
    desc: 'Patient zip codes',
    impact: 'Unlocks zip-code-level opportunity sizing',
    icon: MapPin,
    example: (sw: string) => `Export from ${sw} → Reports → Patient List by Zip`,
  },
  {
    id: 'financial',
    label: 'Financial Overview',
    desc: 'Total, average, median spend',
    impact: 'Unlocks revenue benchmarking & procedure prioritization',
    icon: DollarSign,
    example: (sw: string) => `Export from ${sw} → Reports → Production by Patient`,
  },
  {
    id: 'insurance',
    label: 'Insurance Mix',
    desc: 'Top carriers, PPO vs Cash',
    impact: 'Unlocks payer-mix strategy & membership plan modeling',
    icon: Stethoscope,
    example: (sw: string) => `Export from ${sw} → Reports → Insurance Analysis`,
  },
];

type EnrichmentLevel = 'basic' | 'enhanced' | 'full';

const LEVELS: Array<{ level: string; levelKey: EnrichmentLevel; desc: string; req: string }> = [
  {
    level: 'Basic',
    levelKey: 'basic',
    desc: 'General market demographics, competitor analysis, census-based insights',
    req: 'No uploads needed',
  },
  {
    level: 'Enhanced',
    levelKey: 'enhanced',
    desc: '+ Practice vs. Market gaps, patient segment analysis, revenue benchmarking',
    req: '1–2 uploads',
  },
  {
    level: 'Full',
    levelKey: 'full',
    desc: '+ Payer-mix strategy, procedure profitability, zip-code ROI, membership plan modeling',
    req: '3–4 uploads',
  },
];

const enrichmentCardClass: Record<EnrichmentLevel, string> = {
  basic: styles.enrichmentCardBasic,
  enhanced: styles.enrichmentCardEnhanced,
  full: styles.enrichmentCardFull,
};

const enrichmentIconClass: Record<EnrichmentLevel, string> = {
  basic: styles.enrichmentIconBasic,
  enhanced: styles.enrichmentIconEnhanced,
  full: styles.enrichmentIconFull,
};

const enrichmentLevelClass: Record<EnrichmentLevel, string> = {
  basic: styles.enrichmentLevelBasic,
  enhanced: styles.enrichmentLevelEnhanced,
  full: styles.enrichmentLevelFull,
};

const enrichmentCountClass: Record<EnrichmentLevel, string> = {
  basic: styles.enrichmentCountBasic,
  enhanced: styles.enrichmentCountEnhanced,
  full: styles.enrichmentCountFull,
};

const levelBoxActiveClass: Record<EnrichmentLevel, string> = {
  basic: styles.levelBoxActiveBasic,
  enhanced: styles.levelBoxActiveEnhanced,
  full: styles.levelBoxActiveFull,
};

const levelNameClass: Record<EnrichmentLevel, string> = {
  basic: styles.levelNameBasic,
  enhanced: styles.levelNameEnhanced,
  full: styles.levelNameFull,
};

export function PracticeDataStep() {
  // Upload instructions and the direct-connect card name this industry's
  // practice-management software — "PMS" for dental, "EHR" for medical.
  const { vertical } = useVertical();
  const { goToStep } = useOutletContext<{ goToStep: (step: number) => void }>();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const {
    data: uploadsRes,
    isLoading: uploadsLoading,
    isError: uploadsError,
    refetch: refetchUploads,
  } = useQuery({
    queryKey: strategyKeys.uploads(locationId),
    queryFn: () => uploadApi.getUploads(locationId),
    enabled: !!locationId,
    // A file still being read (e.g. uploaded from another tab) — keep checking.
    refetchInterval: (query) =>
      query.state.data?.data?.some((u) => u.status === 'processing') ? 5000 : false,
  });

  // When a background extraction finishes, everything computed from the
  // uploads (enrichment, market share, overview, insights) can change too.
  const queryClient = useQueryClient();
  const processingCount = uploadsRes?.data?.filter((u) => u.status === 'processing').length ?? 0;
  const prevProcessing = useRef(processingCount);
  useEffect(() => {
    if (processingCount < prevProcessing.current) {
      invalidateUploadDependents(queryClient, locationId);
    }
    prevProcessing.current = processingCount;
  }, [processingCount, locationId, queryClient]);

  const {
    data: enrichmentRes,
    isLoading: enrichmentLoading,
    isError: enrichmentError,
    refetch: refetchEnrichment,
  } = useQuery({
    queryKey: strategyKeys.enrichment(locationId),
    queryFn: () => uploadApi.getEnrichment(locationId),
    enabled: !!locationId,
  });

  if (uploadsLoading || enrichmentLoading) {
    return (
      <div className={styles.loadingWrapper}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.skeletonRow} />
        ))}
      </div>
    );
  }

  if (uploadsError || enrichmentError) {
    return (
      <EmptyState
        title="Couldn't load your practice data"
        description="Something went wrong loading your uploads. Try again in a moment."
        action={{
          label: 'Retry',
          onClick: () => {
            void refetchUploads();
            void refetchEnrichment();
          },
        }}
      />
    );
  }

  const uploads = uploadsRes?.data ?? [];
  const enrichment = enrichmentRes?.data;
  const uploadMap = new Map(uploads.map((u) => [u.uploadType, u]));

  const uploadCount = enrichment?.uploadCount ?? uploads.length;
  const enrichmentLevel: EnrichmentLevel =
    (enrichment?.level as EnrichmentLevel) ??
    (uploadCount === 0 ? 'basic' : uploadCount < 3 ? 'enhanced' : 'full');

  const lastUpdated = uploads.length
    ? uploads.reduce(
        (latest, u) => (u.updatedAt > latest ? u.updatedAt : latest),
        uploads[0].updatedAt,
      )
    : null;

  return (
    <div className={styles.root}>
      <SectionLabel
        label="Practice Data"
        sub="Your practice data makes the difference between generic market insights and a personalized growth strategy. All data should be de-identified — we only use aggregate-level information to generate your reports."
        timestamp={lastUpdated}
      />

      {/* Enrichment level banner */}
      <Card
        className={`${styles.enrichmentCard} ${enrichmentCardClass[enrichmentLevel]}`}
        padding="md"
      >
        <div className={styles.enrichmentHeader}>
          <div className={styles.enrichmentLeft}>
            <div className={`${styles.enrichmentIconBox} ${enrichmentIconClass[enrichmentLevel]}`}>
              {enrichmentLevel === 'full' ? (
                <Rocket size={18} strokeWidth={1.75} aria-hidden />
              ) : enrichmentLevel === 'enhanced' ? (
                <TrendingUp size={18} strokeWidth={1.75} aria-hidden />
              ) : (
                <BarChart3 size={18} strokeWidth={1.75} aria-hidden />
              )}
            </div>
            <div>
              <div className={styles.enrichmentTitle}>
                Intelligence Level:{' '}
                <span className={enrichmentLevelClass[enrichmentLevel]}>{enrichmentLevel}</span>
              </div>
              <div className={styles.enrichmentSub}>
                {enrichmentLevel === 'basic' &&
                  'Market insights only — add practice data to unlock personalized strategy'}
                {enrichmentLevel === 'enhanced' &&
                  'Good foundation — add more data to maximize insight accuracy'}
                {enrichmentLevel === 'full' && 'Maximum intelligence — all data sources active'}
              </div>
            </div>
          </div>
          <div className={styles.enrichmentRight}>
            <div className={`${styles.enrichmentCount} ${enrichmentCountClass[enrichmentLevel]}`}>
              {uploadCount}/4
            </div>
            <div className={styles.enrichmentCountLabel}>data sources</div>
          </div>
        </div>
        <ProgressBar value={uploadCount} max={4} height={5} />
      </Card>

      {/* Connection options */}
      <div className={styles.connectionGrid}>
        <Card className={styles.connectionCardPms} padding="none">
          <div className={styles.connectionIconMuted}>
            <Link2 size={22} strokeWidth={1.75} aria-hidden />
          </div>
          <div className={styles.connectionTitle}>
            Connect {vertical.practiceSoftwareAbbreviation} Directly
          </div>
          <div className={styles.connectionDesc}>
            Auto-syncs all data. Supports {vertical.practiceSoftware.join(', ')}.
          </div>
          <Badge variant="neutral">Coming Soon</Badge>
        </Card>
        <Card className={styles.connectionCardManual} padding="none">
          <div className={styles.connectionIconAccent}>
            <FileText size={22} strokeWidth={1.75} aria-hidden />
          </div>
          <div className={styles.connectionTitle}>Upload Reports Manually</div>
          <div className={styles.connectionDesc}>
            Export reports from your {vertical.practiceSoftwareAbbreviation} and upload below. CSV,
            XLSX, or PDF accepted.
          </div>
          <Badge variant="primary">Recommended for quick start</Badge>
        </Card>
      </div>

      {/* Upload cards */}
      <SectionLabel
        label="Upload Practice Reports"
        sub="Each upload progressively enriches your market analysis and strategy recommendations."
      />
      <div className={styles.uploadList}>
        {UPLOAD_TYPES.map((ut) => (
          <UploadCard
            key={ut.id}
            uploadType={ut.id}
            label={ut.label}
            description={ut.desc}
            impact={ut.impact}
            icon={ut.icon}
            hint={ut.example(vertical.practiceSoftwareAbbreviation)}
            existing={uploadMap.get(ut.id) ?? null}
          />
        ))}
      </div>

      {/* Level comparison */}
      <Card className={styles.levelsCard}>
        <div className={styles.levelsTitle}>What each level unlocks in your strategy</div>
        <div className={styles.levelsGrid}>
          {LEVELS.map((l) => {
            const isActive = enrichmentLevel === l.levelKey;
            return (
              <div
                key={l.level}
                className={
                  isActive
                    ? `${styles.levelBoxActive} ${levelBoxActiveClass[l.levelKey]}`
                    : styles.levelBox
                }
              >
                <div className={`${styles.levelName} ${levelNameClass[l.levelKey]}`}>{l.level}</div>
                <div className={styles.levelDesc}>{l.desc}</div>
                <div className={styles.levelReq}>{l.req}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Skip link */}
      <div className={styles.skipRow}>
        <span className={styles.skipText}>Don't have these reports handy? </span>
        <button type="button" className={styles.skipLink} onClick={() => goToStep(3)}>
          Skip for now — you can always add data later →
        </button>
      </div>
    </div>
  );
}
